import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Tier, FEATURE_FLAGS } from '@mondaygpt/constants'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components'
import {
  AlertCircle,
  BarChart3,
  Check,
  CreditCard,
  ExternalLink,
  Gem,
  RefreshCw,
  TrendingUp,
  Zap,
} from 'lucide-react'
import { useAnalytics } from 'hooks'
import { selectBusinessId } from 'modules/authentication'
import {
  fetchBusinessBilling,
  openPaywall,
  openTopUpModal,
  selectBusinessEntitlement,
  selectCreditBalance,
  selectCreditBalanceError,
  selectCreditBalanceLoading,
  selectShouldShowUpgrade,
} from 'modules/billing'
import { CreditUsage } from 'modules/billing'
import { CreditsExhaustedNotice } from 'modules/billing/components/CreditsExhaustedNotice'
import { DeleteBusinessCard } from 'modules/billing/components/DeleteBusinessCard'
import { useCreditsExhaustedTopUp } from 'modules/billing/hooks/useCreditsExhaustedTopUp'
import { useFeatureGate } from 'modules/feature-flags'
import {
  CREDITS_ADD_CTA,
  CREDITS_EXHAUSTED_BILLING_DETAIL,
  CREDITS_PURCHASED_POOL_LABEL,
  formatPlanRows,
  PLAN_FEATURES,
} from 'modules/billing/constants'
import { RevenueCatService } from 'modules/billing/services/revenuecat.service'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

/** "1st", "2nd", "3rd", "26th" — day-of-month ordinal for the reset-day copy. */
function ordinalDay(day: number): string {
  const mod100 = day % 100
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`
  switch (day % 10) {
    case 1:
      return `${day}st`
    case 2:
      return `${day}nd`
    case 3:
      return `${day}rd`
    default:
      return `${day}th`
  }
}

/** Parse the ISO renewal timestamp; null when absent or unparseable. */
function parseRenewal(iso?: string): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

function CreditBar({ current, total, label }: { current: number; total: number; label: string }) {
  const pct = total > 0 ? Math.min((current / total) * 100, 100) : 0
  const isEmpty = current === 0
  const isLow = !isEmpty && pct <= 20

  const emptyColor = 'text-paper-dim'
  const emptyLabel = '0 remaining'
  const colorClass = isEmpty ? emptyColor : isLow ? 'text-[color:var(--warning)]' : 'text-paper'
  const barClass = isLow ? 'bg-[color:var(--warning)]' : 'bg-acid'

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-paper-dim">{label}</span>
        <span className={`font-medium ${colorClass}`}>
          {isEmpty ? emptyLabel : `${current} remaining`}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-bg-3">
        {/* Bar shows credits REMAINING — empty bucket is an empty bar (0%), not
            a full red one. The card's own red state signals a true block. */}
        <div
          className={`h-full rounded-full transition-all ${barClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export function BillingPage() {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const businessId = useAppSelector(selectBusinessId)
  const balance = useAppSelector(selectCreditBalance)
  const entitlement = useAppSelector(selectBusinessEntitlement)
  const loading = useAppSelector(selectCreditBalanceLoading)
  const error = useAppSelector(selectCreditBalanceError)
  const showUpgrade = useAppSelector(selectShouldShowUpgrade)
  const trackedPageView = useRef(false)
  const [usageOpen, setUsageOpen] = useState(false)
  const usageAnalyticsEnabled = useFeatureGate(FEATURE_FLAGS.creditUsageAnalytics)

  const tier = entitlement?.tier || 'Free'

  useEffect(() => {
    if (!loading && !trackedPageView.current) {
      trackedPageView.current = true
      track(AnalyticsEvents.Billing.BILLING_PAGE_VIEWED, {
        tier,
        credits_remaining: balance?.totalCredits ?? 0,
      })
    }
  }, [loading, tier, balance, track])

  const handleUpgrade = useCallback(() => {
    dispatch(openPaywall({ source: 'billing_page' }))
  }, [dispatch])

  const handleTopUp = useCallback(() => {
    dispatch(openTopUpModal({ source: 'billing_page' }))
  }, [dispatch])

  const handleExhaustedTopUp = useCreditsExhaustedTopUp({
    modalSource: 'billing_page',
    analyticsSource: 'billing_banner',
  })

  const handleManageSubscription = useCallback(async () => {
    track(AnalyticsEvents.Billing.BILLING_MANAGE_SUBSCRIPTION_TAPPED, { tier })
    const service = RevenueCatService.getInstance()
    await service.openManageSubscriptions()
  }, [track, tier])

  const handleRetry = useCallback(() => {
    if (businessId) {
      dispatch(fetchBusinessBilling(businessId))
    }
  }, [dispatch, businessId])

  const isSubscribed = tier !== 'Free'
  const planConfig = PLAN_FEATURES[tier]
  const features = planConfig ? formatPlanRows(planConfig) : []
  const monthlyCredits = entitlement?.product?.monthlyCredits || 0
  const hasNoCredits = (balance?.totalCredits ?? 0) === 0 && isSubscribed
  // Name the billing PERIOD on the plan card. Credits are granted monthly on
  // every plan, so a bare "300 credits/month" read as a monthly subscription to
  // yearly payers (top refund driver — plan/renewal confusion). `product.period`
  // is 'Monthly' | 'Yearly'; surface it so "Billed yearly · 300 credits a month"
  // can't be mistaken for a monthly plan. (The concrete renewal date is a fast
  // follow-up — it isn't on the client yet, it lives on the RC subscription.)
  const billingPeriod = entitlement?.product?.period
  const planSubtitle = !isSubscribed
    ? 'No active subscription'
    : billingPeriod
      ? `Billed ${billingPeriod.toLowerCase()} · ${monthlyCredits} credits a month`
      : `${monthlyCredits} credits a month`

  // Concrete renewal date + monthly reset day, when the vendor gave us a period
  // end. Yearly plans renew on `renewsAt`; monthly credits reset on its
  // day-of-month every month. Falls back to the generic line when absent.
  const renewalDate = parseRenewal(entitlement?.renewsAt)
  const renewsLabel =
    isSubscribed && renewalDate
      ? renewalDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null
  const creditsResetLabel = renewalDate
    ? `Credits reset on the ${ordinalDay(renewalDate.getDate())} of each month.`
    : 'Credits reset at the start of each billing month.'

  let body: ReactNode

  if (loading) {
    body = <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-acid" />
  } else if (error) {
    body = (
      <div className="space-y-4 text-center">
        <AlertCircle size={32} className="mx-auto text-[color:var(--alert-error-text)]" />
        <p className="text-sm text-paper-dim">Failed to load billing information</p>
        <button
          onClick={handleRetry}
          className="inline-flex items-center gap-2 rounded-[8px] border border-line-bright bg-bg-2 px-4 py-2 text-[13px] font-medium text-paper transition-colors hover:bg-bg-3"
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    )
  } else {
    body = (
      <div className="mx-auto w-full max-w-[640px] space-y-6 px-6 py-8">
        {/* Header */}
        <div>
          <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Billing
          </span>
          <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
            Plan & credits
          </h1>
        </div>

        {/* Zero-credit system notice */}
        {hasNoCredits && (
          <CreditsExhaustedNotice
            variant="card-detail"
            detail={CREDITS_EXHAUSTED_BILLING_DETAIL}
            onTopUp={handleExhaustedTopUp}
          />
        )}

        {/* Current Plan */}
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-line-bright bg-bg-3 text-acid">
                {isSubscribed ? <Gem size={18} /> : <CreditCard size={18} />}
              </div>
              <div>
                <h2 className="text-[16px] font-semibold tracking-[-0.005em] text-paper">
                  {tier} Plan
                </h2>
                <p className="text-[13px] text-paper-dim">{planSubtitle}</p>
              </div>
            </div>
            {isSubscribed && (
              <span className="bg-acid-tint mono rounded-full border border-acid/40 px-3 py-1 text-[10px] tracking-[0.05em] text-acid uppercase">
                Active
              </span>
            )}
          </div>

          {renewsLabel && <p className="mt-4 text-[12px] text-paper-faint">Renews {renewsLabel}</p>}

          {isSubscribed && (
            <button
              onClick={handleManageSubscription}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-[9px] border border-line-bright px-4 py-2.5 text-[13px] font-medium text-paper transition-colors hover:bg-bg-3"
            >
              Manage subscription
              <ExternalLink size={14} />
            </button>
          )}
        </div>

        {/* Credit Balance */}
        {balance && (
          <div className="space-y-4 rounded-2xl border border-line bg-bg-2 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <h3 className="text-sm font-semibold text-paper">Credits</h3>
                {businessId && usageAnalyticsEnabled && (
                  <button
                    onClick={() => setUsageOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line-bright px-2.5 py-1 text-[11px] font-medium text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
                  >
                    <BarChart3 size={13} />
                    Analytics
                  </button>
                )}
              </div>
              <span className="text-2xl font-bold text-paper">{balance.totalCredits}</span>
            </div>

            {isSubscribed && monthlyCredits > 0 && (
              <CreditBar
                current={balance.subscriptionCredits}
                total={monthlyCredits}
                label="Monthly credits"
              />
            )}

            {balance.topUpCredits > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-paper-dim">{CREDITS_PURCHASED_POOL_LABEL}</span>
                <span className="font-medium text-paper">{balance.topUpCredits}</span>
              </div>
            )}

            <p className="text-xs text-paper-faint">
              1 credit = 1 AI generation. {creditsResetLabel}
            </p>

            {isSubscribed && !hasNoCredits && (
              <button type="button" onClick={handleTopUp} className="btn-acid-outline mt-1 w-full">
                {CREDITS_ADD_CTA}
              </button>
            )}
          </div>
        )}

        {/* Plan Features */}
        {isSubscribed && features.length > 0 && (
          <div className="rounded-2xl border border-line bg-bg-2 p-6">
            <h3 className="text-sm font-semibold text-paper">Your plan includes</h3>
            <ul className="mt-3 space-y-2.5">
              {features.map(feature => (
                <li key={feature} className="flex items-center gap-2.5 text-sm text-paper-dim">
                  <Check size={16} className="flex-shrink-0 text-acid" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upgrade to Business */}
        {tier === 'Pro' && (
          <div className="rounded-2xl border border-[color:color-mix(in_srgb,var(--acid)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--acid)_6%,var(--bg-2))] p-6">
            <div className="mb-4">
              <div className="mb-1 flex items-center gap-2">
                <TrendingUp size={18} className="text-acid" />
                <h3 className="text-lg font-semibold text-paper">Upgrade to Business</h3>
              </div>
              <p className="text-sm text-paper-dim">3× the credits, more seats.</p>
            </div>
            <ul className="mb-5 space-y-2.5">
              {/* Only the rows that differ from Pro — derived so plan-fact
                  changes in PLAN_FEATURES can't leave this card stale. */}
              {formatPlanRows(PLAN_FEATURES[Tier.Business])
                .filter(row => !formatPlanRows(PLAN_FEATURES[Tier.Pro]).includes(row))
                .map(feature => (
                  <li key={feature} className="flex items-center gap-2.5 text-sm text-paper">
                    <Check size={16} className="flex-shrink-0 text-acid" />
                    {feature}
                  </li>
                ))}
            </ul>
            <button
              onClick={() => {
                track(AnalyticsEvents.Billing.BILLING_UPGRADE_TO_BUSINESS_TAPPED, {
                  credits_remaining: balance?.totalCredits ?? 0,
                })
                handleUpgrade()
              }}
              className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-xl bg-acid px-5 py-3 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px"
            >
              <Zap size={16} />
              Upgrade to Business
            </button>
          </div>
        )}

        {/* Subscribe CTA (for free users) */}
        {showUpgrade && tier === 'Free' && (
          <div className="rounded-2xl border border-[color:color-mix(in_srgb,var(--acid)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--acid)_6%,var(--bg-2))] p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-paper">Get Started with Taka</h3>
              <p className="mt-1 text-sm text-paper-dim">
                Professional branded content for your business. In seconds.
              </p>
            </div>
            <ul className="mb-5 space-y-2.5">
              {formatPlanRows(PLAN_FEATURES[Tier.Pro]).map(feature => (
                <li key={feature} className="flex items-center gap-2.5 text-sm text-paper">
                  <Check size={16} className="flex-shrink-0 text-acid" />
                  {feature}
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                track(AnalyticsEvents.Billing.BILLING_CHOOSE_PLAN_TAPPED, {})
                handleUpgrade()
              }}
              className="text-ink-on-acid flex w-full items-center justify-center gap-2 rounded-xl bg-acid px-5 py-3 text-[13px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px"
            >
              <Zap size={16} />
              Choose a plan
            </button>
          </div>
        )}

        {/* Delete business (owner-only) — lives here, not in the switcher */}
        <DeleteBusinessCard />

        {/* Usage analytics modal */}
        {businessId && usageAnalyticsEnabled && (
          <Dialog open={usageOpen} onOpenChange={setUsageOpen}>
            <DialogContent variant="dark" size="lg" className="taka-dark">
              <DialogHeader className="text-left">
                <DialogTitle variant="dark">Credit usage</DialogTitle>
                <DialogDescription variant="dark">
                  How your business is using generation credits.
                </DialogDescription>
              </DialogHeader>
              <div className="-mr-2 max-h-[70vh] overflow-y-auto pr-2">
                <CreditUsage businessId={businessId} embedded />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    )
  }

  return (
    // `taka-dark` scopes the T3 pattern classes (.card, .mono) and font
    // features, but it is theme-aware — it follows the user's chosen theme via
    // the html-level `data-theme`. Unlike the paywall / top-up dialogs (which
    // stay pinned to dark via PricingThemeSurface), this in-app billing surface
    // is NOT pinned, so Plan & credits renders in light when the user picks it.
    <div
      className={`taka-dark ${
        loading || error
          ? 'flex h-full flex-col items-center justify-center px-4'
          : 'flex h-full flex-col overflow-y-auto'
      }`}
    >
      {body}
    </div>
  )
}
